import { useEffect, useMemo } from "react";
import { isEn } from "i18next-config";
import { useFormikContext } from "formik";
import {
  actionFormValue,
  actionInputName,
} from "components/action-modal/utils/inputName";
import { sortObjectsByProperty } from "utils/sortUtils";
import { snakeToCamel } from "utils/camelSnakeTransformation";

const FERTILIZER_TYPE_BY_OPERATION = {
  hydrolubrication: "hydro",
  soilBasic: "groundBasic",
  soilSurface: "groundSurface",
  foliar: "foliar",
};

const useFertilizeTypes = (operationOptions) => {
  const fertilizeTypes = useMemo(() => {
    const filters = {};

    operationOptions.forEach(({ value: id, operationType }) => {
      const fertilizerType = FERTILIZER_TYPE_BY_OPERATION[operationType];
      if (fertilizerType) {
        filters[id] = fertilizerType;
      }
    });

    return filters;
  }, [operationOptions]);

  return { fertilizeTypes };
};

export const useFilteredFertilizers = ({
  fertilizers,
  operationOptions,
  isProposed,
  isViewMode,
}) => {
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const { fertilizeTypes } = useFertilizeTypes(operationOptions);
  const selectedOperationTypeId = actionFormValue(
    values,
    "fertilizeAction",
    "operationTypeId",
    isProposed,
  );
  const selectedFertilizeType = fertilizeTypes[selectedOperationTypeId];
  const selectedBio = actionFormValue(
    values,
    "fertilizeAction",
    "bio",
    isProposed,
  );
  const selectedAgroMetaValue = actionFormValue(
    values,
    "fertilizeAction",
    "agroMetaValue",
    isProposed,
  );
  const selectedAgroMetaKeys = useMemo(
    () =>
      (Array.isArray(selectedAgroMetaValue)
        ? selectedAgroMetaValue
        : [selectedAgroMetaValue]
      )
        .filter(Boolean)
        .map(snakeToCamel),
    [selectedAgroMetaValue],
  );

  const filteredFertilizers = useMemo(
    () =>
      Array.isArray(fertilizers)
        ? fertilizers.filter(
            (fertilizer) =>
              (!selectedFertilizeType || fertilizer[selectedFertilizeType]) &&
              (selectedBio === undefined ||
                selectedBio === null ||
                fertilizer.bio === selectedBio) &&
              (!selectedAgroMetaKeys.length ||
                selectedAgroMetaKeys.some((key) => fertilizer[key])),
          )
        : [],
    [fertilizers, selectedFertilizeType, selectedBio, selectedAgroMetaKeys],
  );

  const fertilizerFieldName = (fieldName) =>
    actionInputName("fertilizeAction", fieldName, isProposed);

  const clearFertilizerField = (fieldName) => {
    const inputName = fertilizerFieldName(fieldName);
    setFieldValue(inputName, null).then(() => {
      setFieldTouched(inputName, true);
    });
  };

  useEffect(() => {
    if (isViewMode || !Array.isArray(fertilizers)) return;

    const currentFertId = actionFormValue(
      values,
      "fertilizeAction",
      "fertId",
      isProposed,
    );
    if (!currentFertId) return;

    const isSelectedFertilizerAvailable = filteredFertilizers.some(
      ({ ourId }) => ourId === currentFertId,
    );
    if (isSelectedFertilizerAvailable) return;

    ["fertId", "brandName", "composition"].forEach(clearFertilizerField);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fertilizers,
    filteredFertilizers,
    isProposed,
    isViewMode,
    values?.fertilizeAction?.fertId,
    values?.proposedAction?.fertId,
  ]);

  const handleFertilizerChange = (value) => {
    const newValue = value?.value;
    const fertIdInputName = fertilizerFieldName("fertId");
    setFieldValue(fertIdInputName, newValue).then(() => {
      setFieldTouched(fertIdInputName, true);
    });

    const selectedFertilizer = filteredFertilizers.find(
      ({ ourId }) => ourId === newValue,
    );

    const brandNameInputName = fertilizerFieldName("brandName");
    setFieldValue(brandNameInputName, selectedFertilizer?.brandName).then(
      () => {
        setFieldTouched(brandNameInputName, true);
      },
    );
    const compositionInputName = fertilizerFieldName("composition");
    setFieldValue(compositionInputName, selectedFertilizer?.composition).then(
      () => {
        setFieldTouched(compositionInputName, true);
      },
    );
  };

  const fertilizerOptions = useMemo(
    () =>
      filteredFertilizers
        .map(({ ourId, brandName, name, description }) => ({
          value: ourId,
          label:
            ((isEn ? name.nameEn : name.nameGr) || brandName) +
            ", " +
            description,
        }))
        .sort((a, b) => sortObjectsByProperty(a, b, "label")),
    [filteredFertilizers],
  );

  return {
    fertilizerOptions,
    handleFertilizerChange,
  };
};
